import { useState, useCallback } from 'react'
import axios from 'axios'
import { extractErrorMessage } from '../utils/apitools'
import { CarbonInterviewRawData, CarbonInterviewRawDataRow } from '../types/gotypes'
import useAccount from './useAccount'
import { API_BASE_URL } from '../constants/system'

export interface UpdateData {
  variable?: string
  filter?: string
  page?: number
  rowsPerPage?: number
}

export const useDataExplorer = ({
  customer,
  job,
  onDataChange,
}: {
  customer: string
  job: string
  onDataChange?: (data: CarbonInterviewRawDataRow[], totalCount: number) => void
}) => {
  const account = useAccount()
  
  const [data, setData] = useState<CarbonInterviewRawDataRow[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(100)
  const [variable, setVariable] = useState('')
  const [filter, setFilter] = useState('')

  const runQuery = useCallback(async (data: Partial<UpdateData>) => {
    if(!customer || !job || !account.user) return
    setIsLoading(true)
    setError(null)
    try {
      const res = await axios.get<CarbonInterviewRawData>(`${API_BASE_URL}/data/${customer}/${job}/query?varnames=${data.variable}&filter=${data.filter}&offset=${(data.page || 0) * (data.rowsPerPage || 100)}&limit=${data.rowsPerPage || 100}`)
      const {
        total,
        rows,
      } = res.data
      setTotalRows(total)
      setData(rows)
      onDataChange?.(rows, total)
    } catch (error) {
      const extracted = extractErrorMessage(error)
      setData([])
      setError(new Error(extracted))
    } finally {
      setIsLoading(false)
    }
  }, [customer, job, account.user])

  const update = useCallback((data: Partial<UpdateData> = {}) => {
    const useData = {
      variable: data.variable || variable,
      filter: data.filter || filter,
      page: data.page || page,
      rowsPerPage: data.rowsPerPage || rowsPerPage,
    }
    if(data.variable) {
      setVariable(data.variable)
    }
    if(data.filter) {
      setFilter(data.filter)
    }
    if(typeof(data.page) === 'number') {
      setPage(data.page)
    }
    if(typeof(data.rowsPerPage) === 'number') {
      setRowsPerPage(data.rowsPerPage)
    }
    return useData
  }, [variable, filter, page, rowsPerPage])

  const updateAndRunQuery = useCallback((data: Partial<UpdateData> = {}) => {
    runQuery(update(data))
  }, [runQuery, update])

  return {
    runQuery,
    update,
    updateAndRunQuery,
    data,
    totalRows,
    isLoading,
    error,
    page, setPage,
    rowsPerPage, setRowsPerPage,
    variable, setVariable,
    filter, setFilter,
  }
}

export default useDataExplorer
